import { SYNC_OPERATION_STATUS } from './flowdeskDataModel.js';
import { getPendingSyncOperations, normalizeSyncQueue } from './syncQueue.js';

export const createLocalOnlySyncTransport = () => ({
  name: 'local-only',
  send: async (operation) => ({
    ok: true,
    skipped: true,
    operationId: operation.id,
    reason: 'No online transport is configured yet.'
  })
});

const getErrorMessage = (error, fallback = 'Unknown sync error') => {
  if (!error) return fallback;
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object') {
    if (typeof error.message === 'string') return error.message;
    if (error.error) return getErrorMessage(error.error, fallback);
    if (error.code || error.message) {
      return [error.code, getErrorMessage(error.message, '')].filter(Boolean).join(': ');
    }
    try {
      return JSON.stringify(error);
    } catch {
      return fallback;
    }
  }
  return String(error);
};

export const runSyncCycle = async ({
  repository,
  transport = createLocalOnlySyncTransport(),
  maxOperations = 25
}) => {
  const queue = normalizeSyncQueue(await repository.loadSyncQueue());
  const pendingOperations = getPendingSyncOperations(queue).slice(0, maxOperations);

  const result = {
    transport: transport.name || 'custom',
    totalQueued: queue.length,
    processed: 0,
    synced: 0,
    failed: 0,
    skipped: 0,
    errors: []
  };

  for (const operation of pendingOperations) {
    result.processed += 1;
    await repository.markSyncOperationStatus({
      operationId: operation.id,
      status: SYNC_OPERATION_STATUS.syncing
    });

    try {
      const response = await transport.send(operation);

      if (response?.ok) {
        await repository.markSyncOperationStatus({
          operationId: operation.id,
          status: SYNC_OPERATION_STATUS.synced
        });

        if (response.skipped) result.skipped += 1;
        else result.synced += 1;
      } else {
        const message = getErrorMessage(response?.error, 'Sync transport rejected the operation.');
        await repository.markSyncOperationStatus({
          operationId: operation.id,
          status: SYNC_OPERATION_STATUS.failed,
          error: message
        });
        result.failed += 1;
        result.errors.push({ operationId: operation.id, message });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      await repository.markSyncOperationStatus({
        operationId: operation.id,
        status: SYNC_OPERATION_STATUS.failed,
        error: message
      });
      result.failed += 1;
      result.errors.push({ operationId: operation.id, message });
    }
  }

  return result;
};

export const createSyncEngine = ({ repository, transport } = {}) => ({
  runOnce: (options = {}) => runSyncCycle({
    repository,
    transport,
    ...options
  })
});
